/**
 * Agent character entity — a seated pixel art character with status-driven animations.
 */

import type { Sprite } from "../sprites/SpriteSheet.js";
import { Entity } from "./Entity.js";
import { AnimationController, type AgentAnimState } from "../animation/AnimationController.js";

export class AgentEntity extends Entity {
  agentId: string;
  agentName: string;
  backend: string;
  isEntryPoint: boolean;
  private animController: AnimationController;

  /** Time accumulator for spawning periodic effects. */
  private effectTimer = 0;

  /** Callback to spawn effects (set by the scene manager). */
  onSpawnEffect?: (type: string, worldX: number, worldY: number) => void;

  constructor(
    agentId: string,
    agentName: string,
    backend: string,
    col: number,
    row: number,
    isEntryPoint: boolean,
    sprites: Record<AgentAnimState, Sprite>,
  ) {
    super(`agent-${agentId}`, col, row, 2); // layer 2 = characters
    this.agentId = agentId;
    this.agentName = agentName;
    this.backend = backend;
    this.isEntryPoint = isEntryPoint;
    this.castsShadow = true;
    this.animController = new AnimationController(sprites);
  }

  /** Change the agent's visual status. */
  setStatus(status: AgentAnimState): void {
    this.animController.transition(status);
    this.effectTimer = 0;
  }

  getStatus(): AgentAnimState {
    return this.animController.getState();
  }

  update(dt: number): void {
    this.animController.tick();
    this.effectTimer += dt;

    // Spawn periodic effects based on state
    const state = this.animController.getState();
    if (this.onSpawnEffect) {
      if (state === "running" && this.effectTimer > 1.5) {
        this.onSpawnEffect("thought-bubble", this.worldX + 12, this.worldY - 20);
        this.effectTimer = 0;
      } else if (state === "error" && this.effectTimer > 0.8) {
        this.onSpawnEffect("smoke", this.worldX + 4, this.worldY - 6);
        this.effectTimer = 0;
      } else if (state === "waiting" && this.effectTimer > 3.0) {
        this.onSpawnEffect("zzz", this.worldX + 10, this.worldY - 18);
        this.effectTimer = 0;
      }
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const { sprite, frameIndex } = this.animController.current();
    const frame = sprite.frames[frameIndex];

    // For terminated state, draw with reduced opacity
    if (this.animController.getState() === "terminated") {
      ctx.globalAlpha = 0.4;
    }

    ctx.drawImage(
      frame,
      this.worldX - sprite.originX,
      this.worldY - sprite.originY,
    );

    ctx.globalAlpha = 1;

    // Entry point badge (small diamond above head)
    if (this.isEntryPoint) {
      const bx = this.worldX;
      const by = this.worldY - sprite.originY - 4;
      ctx.fillStyle = "#ffd700";
      ctx.beginPath();
      ctx.moveTo(bx, by - 3);
      ctx.lineTo(bx + 3, by);
      ctx.lineTo(bx, by + 3);
      ctx.lineTo(bx - 3, by);
      ctx.closePath();
      ctx.fill();
    }

    // Agent name label
    ctx.font = "7px monospace";
    ctx.textAlign = "center";
    ctx.fillStyle = "#a1a1aa";
    ctx.fillText(
      this.agentName,
      this.worldX,
      this.worldY + (sprite.height - sprite.originY) + 8,
    );

    // Status glow under character for running state
    if (this.animController.getState() === "running") {
      ctx.save();
      const gradient = ctx.createRadialGradient(
        this.worldX, this.worldY + 4, 0,
        this.worldX, this.worldY + 4, 16,
      );
      gradient.addColorStop(0, "rgba(128, 0, 255, 0.15)");
      gradient.addColorStop(1, "rgba(128, 0, 255, 0)");
      ctx.fillStyle = gradient;
      ctx.fillRect(this.worldX - 16, this.worldY - 12, 32, 32);
      ctx.restore();
    }
  }
}
